import React, { useState } from 'react';
import ContentEditForm from '../components/ContentEditForm';
import {
  Card,
  Table,
  Button,
  Space,
  Tag,
  Input,
  Select,
  Modal,
  Upload,
  message,
  Row,
  Col,
  Statistic,
  Descriptions,
  Tooltip,
  Alert,
} from 'antd';
import {
  VideoCameraOutlined,
  SoundOutlined,
  FileTextOutlined,
  ReloadOutlined,
  EyeOutlined,
  EditOutlined,
  DeleteOutlined,
  DownloadOutlined,
  FileExcelOutlined,
} from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import * as XLSX from 'xlsx';
import { adminService, Content, FilterContentsDto } from '../services/admin';

const { Option } = Select;
const { Search } = Input;

const ContentList: React.FC = () => {
  const [filters, setFilters] = useState<FilterContentsDto>({ page: 1, limit: 10 });
  const [selectedContent, setSelectedContent] = useState<Content | null>(null);
  const [modalMode, setModalMode] = useState<'view' | 'edit'>('view');
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [excelUploading, setExcelUploading] = useState(false);
  const [importResult, setImportResult] = useState<{
    success: number;
    errors: any[];
    totalProcessed: number;
  } | null>(null);
  const [fileIdSearch, setFileIdSearch] = useState<string>('');
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);

  const queryClient = useQueryClient();

  const parseMaybeDoubleEncodedJson = (raw: string): any => {
    const trimmed = raw.trim();
    if (!trimmed) return null;
    try {
      const first = JSON.parse(trimmed);
      if (typeof first === 'string') {
        const inner = first.trim();
        if (!inner) return first;
        try {
          return JSON.parse(inner);
        } catch {
          return first;
        }
      }
      return first;
    } catch {
      return null;
    }
  };

  const formatMetadataPretty = (metadata: any): string => {
    if (metadata == null) return '-';
    try {
      if (typeof metadata === 'string') {
        const parsed = parseMaybeDoubleEncodedJson(metadata);
        if (parsed && typeof parsed === 'object') return JSON.stringify(parsed, null, 2);
        if (typeof parsed === 'string' && parsed.trim()) return parsed;
        return metadata;
      }
      if (typeof metadata === 'object') return JSON.stringify(metadata, null, 2);
      return String(metadata);
    } catch {
      return typeof metadata === 'string' ? metadata : String(metadata);
    }
  };

  const formatMetadataInline = (metadata: any): string => {
    const pretty = formatMetadataPretty(metadata);
    if (pretty === '-') return '-';
    const oneLine = pretty.replace(/\s+/g, ' ').trim();
    return oneLine.length > 40 ? `${oneLine.slice(0, 40)}...` : oneLine;
  };

  const refreshCurrentList = () => {
    if (fileIdSearch.trim()) {
      fileIdRefetch();
      return;
    }
    refetch();
  };

  // Fetch content statistics
  const { data: stats, refetch: refetchStats } = useQuery({
    queryKey: ['content-stats'],
    queryFn: adminService.getContentStats,
  });

  // Search content by fileId (if fileIdSearch is populated)
  const {
    data: fileIdSearchResult,
    isLoading: fileIdLoading,
    refetch: fileIdRefetch,
    error: fileIdError
  } = useQuery({
    queryKey: ['contents-by-fileId', fileIdSearch],
    queryFn: () => adminService.searchContentByFileId(fileIdSearch),
    enabled: !!fileIdSearch.trim(), // Only enabled when fileIdSearch has value
  });

  // Fetch content list with filters (used when no fileId search)
  const { data: contents, refetch, isLoading, error: contentsError } = useQuery({
    queryKey: ['contents', filters],
    queryFn: () => adminService.getContents(filters),
    enabled: !fileIdSearch.trim(), // Only enabled when no fileId search
  });

  // Update content mutation
  const updateContentMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: any }) => adminService.updateContent(id, data),
    onSuccess: () => {
      message.success('内容更新成功');
      setIsModalVisible(false);
      refreshCurrentList();
    },
    onError: (error: any) => {
      const err = error as any;
      message.error(err.response?.data?.message || '更新失败');
    },
  });

  // Delete content mutation
  const deleteContentMutation = useMutation({
    mutationFn: adminService.deleteContent,
    onSuccess: (deleted) => {
      message.success('内容删除成功');
      refreshCurrentList();
      refetchStats();
      setSelectedRowKeys((prev) => prev.filter((key) => key !== deleted.id));
    },
    onError: (error: any) => {
      const err = error as any;
      message.error(err.response?.data?.message || '删除失败');
    },
  });

  const batchDeleteContentsMutation = useMutation({
    mutationFn: adminService.batchDeleteContents,
    onSuccess: (result) => {
      message.success(`已删除 ${result.deletedCount} 条内容`);
      setSelectedRowKeys([]);
      queryClient.invalidateQueries({ queryKey: ['contents'] });
      queryClient.invalidateQueries({ queryKey: ['contents-by-fileId'] });
      refreshCurrentList();
      refetchStats();
    },
    onError: (error: any) => {
      const err = error as any;
      message.error(err.response?.data?.message || '批量删除失败');
    },
  });

  const clearContentsMutation = useMutation({
    mutationFn: adminService.clearContents,
    onSuccess: (result) => {
      message.success(`已清空 ${result.deletedCount} 条内容`);
      setSelectedRowKeys([]);
      setFileIdSearch('');
      setFilters((prev) => ({ ...prev, page: 1 }));
      queryClient.invalidateQueries({ queryKey: ['contents'] });
      refetchStats();
    },
    onError: (error: any) => {
      const err = error as any;
      message.error(err.response?.data?.message || '清空失败');
    },
  });

  // Excel import mutation
  const importExcelMutation = useMutation({
    mutationFn: adminService.importExcel,
    onSuccess: (result) => {
      setImportResult(result);
      if (result.success > 0) {
        message.success(`成功导入 ${result.success} 条内容记录`);
      }
      if (result.errors.length > 0) {
        message.warning(`${result.errors.length} 条记录导入失败`);
      }
      refetch();
      if (fileIdSearch.trim()) {
        fileIdRefetch();
      }
    },
    onError: (error: any) => {
      const err = error as any;
      message.error(err.response?.data?.message || 'Excel导入失败');
    },
  });

  const getContentTypeIcon = (contentType: string) => {
    switch (contentType) {
      case 'video':
        return <VideoCameraOutlined style={{ color: '#ff4d4f', fontSize: 24 }} />;
      case 'audio':
        return <SoundOutlined style={{ color: '#1890ff', fontSize: 24 }} />;
      case 'document':
        return <FileTextOutlined style={{ color: '#52c41a', fontSize: 24 }} />;
      default:
        return <FileTextOutlined style={{ color: '#666', fontSize: 24 }} />;
    }
  };

  const getContentTypeColor = (contentType: string) => {
    switch (contentType) {
      case 'video':
        return 'red';
      case 'audio':
        return 'blue';
      case 'document':
        return 'green';
      default:
        return 'gray';
    }
  };

  const formatDuration = (seconds: number) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const remainingSeconds = seconds % 60;

    if (hours > 0) return `${hours}h ${minutes}m ${remainingSeconds}s`;
    if (minutes > 0) return `${minutes}m ${remainingSeconds}s`;
    return `${remainingSeconds}s`;
  };

  // Handle fileId search
  const handleFileIdSearch = (value: string) => {
    // Clear fileId search when empty
    if (!value.trim()) {
      setFileIdSearch('');
      // Reset filters to ensure regular content fetch resumes
      setFilters(prev => ({ ...prev, page: 1 }));
      return;
    }

    setFileIdSearch(value.trim());
  };

  // Get the correct data source based on whether we're doing file ID search
  const getCurrentContents = () => {
    if (fileIdSearch.trim()) {
      return fileIdSearchResult;
    }
    return contents;
  };

  // Get the correct loading state
  const getCurrentLoading = () => {
    if (fileIdSearch.trim()) {
      return fileIdLoading;
    }
    return isLoading;
  };

  const openView = (content: Content) => {
    setSelectedContent(content);
    setModalMode('view');
    setIsModalVisible(true);
  };

  const openEdit = (content: Content) => {
    setSelectedContent(content);
    setModalMode('edit');
    setIsModalVisible(true);
  };

  const handleDelete = (content: Content) => {
    deleteContentMutation.mutate(content.id);
  };

  const handleModalSubmit = async (values: any) => {
    if (!selectedContent) return;
    updateContentMutation.mutate({ id: selectedContent.id, data: values });
  };

  const handleBatchDelete = () => {
    const ids = selectedRowKeys.map((k) => Number(k)).filter((n) => !Number.isNaN(n));
    if (!ids.length) return;

    Modal.confirm({
      title: '确定要删除选中的内容吗？',
      content: `将删除 ${ids.length} 条内容记录，此操作不可恢复。`,
      okText: '删除',
      okButtonProps: { danger: true },
      cancelText: '取消',
      onOk: () => batchDeleteContentsMutation.mutate(ids),
    });
  };

  const handleClear = () => {
    Modal.confirm({
      title: '确定要一键清空内容库吗？',
      content: '将删除所有内容记录，此操作不可恢复。',
      okText: '清空',
      okButtonProps: { danger: true },
      cancelText: '取消',
      onOk: () => clearContentsMutation.mutate(),
    });
  };

  const handleExcelUpload = (file: File) => {
    setExcelUploading(true);
    setImportResult(null); // Clear previous results
    importExcelMutation.mutate(file);
    return false; // Prevent default upload behavior
  };

  React.useEffect(() => {
    if (importExcelMutation.isSuccess || importExcelMutation.isError) {
      setExcelUploading(false);
    }
  }, [importExcelMutation.isSuccess, importExcelMutation.isError]);

  React.useEffect(() => {
    if (fileIdError) {
      const error = fileIdError as any;
      message.error(error.response?.data?.message || '文件ID搜索失败');
    }
  }, [fileIdError]);

  React.useEffect(() => {
    if (contentsError) {
      const error = contentsError as any;
      message.error(error.response?.data?.message || '内容列表加载失败');
    }
  }, [contentsError]);

  const downloadExcelTemplate = () => {
    const templateData = adminService.downloadExcelTemplate();
    const worksheet = XLSX.utils.json_to_sheet(templateData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, '内容模板');

    // Auto-size columns (updated for new fields)
    const colWidths = [
      { wch: 15 }, // 文件ID
      { wch: 25 }, // 文件名
      { wch: 12 }, // 内容类型
      { wch: 12 }, // 分辨率
      { wch: 8 },  // 时长
      { wch: 10 }, // 码率
      { wch: 25 }, // 元数据
    ];
    worksheet['!cols'] = colWidths;

    XLSX.writeFile(workbook, '内容导入模板.xlsx');
    message.success('Excel模板下载成功');
  };

  const columns = [
    {
      title: '文件ID',
      dataIndex: 'fileId',
      key: 'fileId',
      width: 120,
      ellipsis: true,
      render: (fileId: string) => (
        <Tooltip title={fileId}>
          <span>{fileId.substring(0, 12)}...</span>
        </Tooltip>
      ),
    },
    {
      title: '文件名',
      dataIndex: 'filename',
      key: 'filename',
      width: 200,
      render: (text: string, record: Content) => (
        <Space>
          {getContentTypeIcon(record.contentType)}
          <span style={{ fontWeight: 500 }}>{text}</span>
        </Space>
      ),
    },
    {
      title: '内容类型',
      dataIndex: 'contentType',
      key: 'contentType',
      width: 100,
      render: (contentType: string) => (
        <Tag color={getContentTypeColor(contentType)}>
          {contentType.toUpperCase()}
        </Tag>
      ),
    },
    {
      title: '分辨率',
      dataIndex: 'resolution',
      key: 'resolution',
      width: 120,
      render: (resolution?: string) => resolution || '-',
    },
    {
      title: '时长',
      dataIndex: 'duration',
      key: 'duration',
      width: 80,
      render: (duration?: number) => duration ? formatDuration(duration) : '-',
    },
    {
      title: '码率',
      dataIndex: 'bitRate',
      key: 'bitRate',
      width: 100,
      render: (bitRate?: number) => bitRate ? `${(bitRate / 1000).toFixed(0)} kbps` : '-',
    },
    {
      title: '元数据',
      dataIndex: 'metadata',
      key: 'metadata',
      width: 120,
      ellipsis: true,
      render: (metadata?: any) => {
        const pretty = formatMetadataPretty(metadata);
        return (
          <Tooltip
            title={
              <pre style={{ margin: 0, whiteSpace: 'pre-wrap', maxWidth: 520 }}>
                {pretty}
              </pre>
            }
          >
            <span>{formatMetadataInline(metadata)}</span>
          </Tooltip>
        );
      },
    },
    {
      title: '创建时间',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 160,
      render: (date: string) => new Date(date).toLocaleString('zh-CN'),
    },
    {
      title: '更新时间',
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      width: 160,
      render: (date: string) => new Date(date).toLocaleString('zh-CN'),
    },
    {
      title: '操作',
      key: 'actions',
      width: 120,
      fixed: 'right' as const,
      render: (text: any, record: Content) => (
        <Space size="small">
          <Button size="small" icon={<EyeOutlined />} onClick={() => openView(record)}>
            查看
          </Button>
          <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(record)}>
            编辑
          </Button>
          <Button danger size="small" icon={<DeleteOutlined />} onClick={() => handleDelete(record)}>
            删除
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col span={6}>
          <Card>
            <Statistic
              title="总内容数"
              value={stats?.totalContents || 0}
              prefix={getContentTypeIcon('document')}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="视频内容"
              value={stats?.contentTypesDistribution?.video || 0}
              prefix={getContentTypeIcon('video')}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="音频内容"
              value={stats?.contentTypesDistribution?.audio || 0}
              prefix={getContentTypeIcon('audio')}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Button
              type="primary"
              icon={<ReloadOutlined />}
              onClick={() => { refetch(); refetchStats(); }}
              style={{ width: '100%', marginBottom: 8 }}
            >
              刷新数据
            </Button>
            {stats?.contentTypesDistribution && Object.entries(stats.contentTypesDistribution).map(([type, count]) => (
              <div key={type} style={{ fontSize: '12px', marginBottom: 4 }}>
                <span style={{ color: '#666' }}>{type}:</span> {count}
              </div>
            ))}
          </Card>
        </Col>
      </Row>

      {/* Excel Import Results */}
      {importResult && (
        <Card title="Excel导入结果" style={{ marginBottom: 24 }}>
          <Row gutter={[16, 16]}>
            <Col span={8}>
              <Statistic
                title="成功导入"
                value={importResult.success}
                valueStyle={{ color: '#3f8600' }}
              />
            </Col>
            <Col span={8}>
              <Statistic
                title="导入失败"
                value={importResult.errors.length}
                valueStyle={{ color: '#cf1322' }}
              />
            </Col>
            <Col span={8}>
              <Statistic
                title="总计处理"
                value={importResult.totalProcessed}
              />
            </Col>
          </Row>

          {importResult.errors.length > 0 && (
            <Alert
              message="导入错误详情"
              description={
                <div style={{ maxHeight: 200, overflow: 'auto' }}>
                  {importResult.errors.map((error, index) => (
                    <div key={index} style={{ marginBottom: 8 }}>
                      <strong>第{error.row}行</strong> (文件ID: {error.fileId}): {error.error}
                    </div>
                  ))}
                </div>
              }
              type="error"
              style={{ marginTop: 16 }}
            />
          )}
        </Card>
      )}

      <Card title="内容管理">
        <div style={{ marginBottom: 16 }}>
          <Space>
            <Search
              placeholder="搜索文件名"
              value={filters.filename}
              onChange={(e) => setFilters({ ...filters, filename: e.target.value })}
              onSearch={() => refetch()}
              style={{ width: 250 }}
              loading={getCurrentLoading()}
            />
            <Search
              placeholder="搜索文件ID"
              value={fileIdSearch}
              onChange={(e) => handleFileIdSearch(e.target.value)}
              onSearch={(value) => handleFileIdSearch(value)}
              style={{ width: 250 }}
              loading={getCurrentLoading()}
            />
            <Select
              placeholder="内容类型"
              value={filters.contentType}
              onChange={(value) => setFilters({ ...filters, contentType: value })}
              style={{ width: 120 }}
              allowClear
            >
              <Option value="video">视频</Option>
              <Option value="audio">音频</Option>
              <Option value="document">文档</Option>
            </Select>
            <Select
              placeholder="分辨率"
              value={filters.resolution}
              onChange={(value) => setFilters({ ...filters, resolution: value })}
              style={{ width: 120 }}
              allowClear
            >
              <Option value="1920x1080">1920x1080</Option>
              <Option value="1280x720">1280x720</Option>
              <Option value="720x480">720x480</Option>
            </Select>
          </Space>
          <div style={{ float: 'right' }}>
            <Space>
              <Button icon={<DownloadOutlined />} onClick={downloadExcelTemplate}>
                下载Excel模板
              </Button>
              <Upload
                beforeUpload={handleExcelUpload}
                showUploadList={false}
                accept=".xlsx,.xls"
              >
                <Button icon={<FileExcelOutlined />} loading={excelUploading}>
                  Excel导入
                </Button>
              </Upload>
              <Button danger onClick={handleBatchDelete} disabled={!selectedRowKeys.length}>
                多选删除
              </Button>
              <Button danger onClick={handleClear}>
                一键清空
              </Button>
            </Space>
          </div>
        </div>

        <Table
          dataSource={getCurrentContents()?.data || []}
          columns={columns}
          rowKey="id"
          rowSelection={{
            selectedRowKeys,
            onChange: (keys) => setSelectedRowKeys(keys),
          }}
          loading={getCurrentLoading()}
          scroll={{ x: 1200 }}
          pagination={{
            current: getCurrentContents()?.page || 1,
            pageSize: getCurrentContents()?.limit || 10,
            total: getCurrentContents()?.total || 0,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total) => `共 ${total} 条`,
            onChange: (page, pageSize) => setFilters({ ...filters, page, limit: pageSize }),
          }}
        />
      </Card>

      <Modal
        title={modalMode === 'edit' ? '编辑内容' : '查看内容'}
        open={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        footer={null}
        width={700}
      >
        {selectedContent && modalMode === 'view' && (
          <Descriptions bordered column={2}>
            <Descriptions.Item label="ID">{selectedContent.id}</Descriptions.Item>
            <Descriptions.Item label="文件ID">{selectedContent.fileId}</Descriptions.Item>
            <Descriptions.Item label="文件名" span={2}>{selectedContent.filename}</Descriptions.Item>
            <Descriptions.Item label="内容类型">
              <Tag color={getContentTypeColor(selectedContent.contentType)}>
                {selectedContent.contentType.toUpperCase()}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="分辨率">{selectedContent.resolution || '-'}</Descriptions.Item>
            <Descriptions.Item label="时长">{selectedContent.duration ? formatDuration(selectedContent.duration) : '-'}</Descriptions.Item>
            <Descriptions.Item label="码率">{selectedContent.bitRate ? `${(selectedContent.bitRate / 1000).toFixed(0)} kbps` : '-'}</Descriptions.Item>
            <Descriptions.Item label="元数据" span={2}>
              <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>
                {formatMetadataPretty(selectedContent.metadata)}
              </pre>
            </Descriptions.Item>
            <Descriptions.Item label="创建时间">{new Date(selectedContent.createdAt).toLocaleString('zh-CN')}</Descriptions.Item>
            <Descriptions.Item label="更新时间">{new Date(selectedContent.updatedAt).toLocaleString('zh-CN')}</Descriptions.Item>
          </Descriptions>
        )}

        {selectedContent && modalMode === 'edit' && (
          <ContentEditForm
            content={selectedContent}
            onSubmit={handleModalSubmit}
            onCancel={() => setIsModalVisible(false)}
          />
        )}
      </Modal>
    </div>
  );
};

export default ContentList;
